import { ref } from "vue";
import type { ISatMapProps } from "@chatfood/design-system";
import { useNavigation } from "../use-navigation";
import type { IDomainCustomerCoveredAddress } from "~/domain/customer-covered-address";
import type { IDomainNewAddress } from "~/domain/new-address";
import type { IDomainNewUncoveredAddress } from "~/domain/new-uncovered-address";
import type { IDomainCoordinate } from "~/domain/coordinate";
import type { INewAddressEmits } from "~/components/new-address";
import type {
  DeliveryAddress,
  DeliveryLocation,
} from "~/domain/delivery-location";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useGuestStore } from "~/stores/guest";
import { useDirectBackend } from "#imports";

export const useDelivery = () => {
  const { push } = useNavigation();

  const business = useBusinessStore();
  const basket = useBasketStore();
  const customer = useCustomerStore();
  const guest = useGuestStore();

  const { businessRepo, outletRepo } = useDirectBackend();

  const isLoading = ref(false);
  const deliveryAddress = ref<IDomainCustomerCoveredAddress>();
  const deliveryLocation = ref<
    IDomainNewAddress | IDomainNewUncoveredAddress
  >();

  const mapBoundaries = ref<ISatMapProps["mapBounds"]>({
    country: business.country,
  });

  const onDeliveryLocationSelected = (
    location: IDomainNewAddress,
    skipRefocus = false,
  ) => {
    deliveryAddress.value = undefined;
    deliveryLocation.value = location;

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: location.lat,
          lng: location.lng,
        },
      };
    }
  };

  const onUncoveredDeliveryLocationSelected = (
    location: INewAddressEmits["uncoveredAddressSelected"][0],
    skipRefocus = false,
  ) => {
    deliveryAddress.value = undefined;
    deliveryLocation.value = {
      location: location.location,
      lat: location.lat,
      lng: location.lng,
      instruction: "",
      canDeliver: false,
    };

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: location.lat,
          lng: location.lng,
        },
      };
    }
  };

  const onAddressSelected = (
    address: IDomainCustomerCoveredAddress,
    skipRefocus = false,
  ) => {
    deliveryLocation.value = undefined;
    deliveryAddress.value = address;

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: address.lat,
          lng: address.lng,
        },
      };
    }
  };

  const onRefocusRegion = (region: string) => {
    mapBoundaries.value = {
      location: region,
    };
  };

  const onDeliverTo = async (
    address: IDomainCustomerCoveredAddress | IDomainNewAddress,
    location: Omit<DeliveryAddress, "type"> | Omit<DeliveryLocation, "type">,
  ) => {
    isLoading.value = true;

    try {
      const outletId = address.deliveryCoverage.outletId;

      await basket.loadBasket(
        business.id,
        business.currency,
        outletId,
        "delivery",
        business.salesChannel,
      );

      if ("addressId" in location) {
        await basket.setAddress(location);
      } else {
        await basket.setLocation(location);
      }

      if (!customer.isLogged) {
        guest.removeAddress();
      }

      const outlet = await outletRepo.getOutletById({
        outletId,
        salesChannel: basket.salesChannel,
        orderingMode: "delivery",
      });

      await push("outlet", {
        orderingMode: "delivery",
        outletSlug: outlet.slug,
      });
    } catch (e) {
      isLoading.value = false;
      throw e;
    }
  };

  const getCoordinateCoverage = async (coordinate: IDomainCoordinate) => {
    try {
      return await businessRepo.getCoverageForCoordinates({
        businessId: business.id,
        salesChannel: business.salesChannel,
        lat: coordinate.lat,
        lng: coordinate.lng,
      });
    } catch {
      // toast
      // report
    }
  };

  return {
    mapBoundaries,
    isLoading,
    deliveryAddress,
    deliveryLocation,
    onDeliveryLocationSelected,
    onUncoveredDeliveryLocationSelected,
    onAddressSelected,
    onRefocusRegion,
    onDeliverTo,
    getCoordinateCoverage,
  };
};
